import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormField, Modal, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react/polaris';
import ErrorUtils from 'src/utils/ErrorUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { UploadSegmentDataTableResponse, ValidationStatus } from 'src/models/segment-cdt/SegmentCdtTypes';

/** Sum of the three rejection counters (null unknown-segment count is not a rejection here). */
function rejectedCount(r: UploadSegmentDataTableResponse): number {
    return (r.nonEditableColumnsRejected ?? 0) + (r.unknownSegmentsRejected ?? 0) + (r.valuesOutOfBounds ?? 0);
}

/**
 * A clean success: rows were written, nothing was rejected, and neither check reported a problem. A
 * WARNING or FAILED status on either check keeps it out of the green "success" state so the warning isn't
 * buried behind a success banner (and doesn't contradict the "(check WARNING)" line in the body). An absent
 * or NOT_APPLICABLE segment-master status is not a problem, so it stays clean.
 */
function isCleanUpload(r: UploadSegmentDataTableResponse): boolean {
    const validation = r.validationReport?.overallStatus;
    const unknownCheck = r.unknownSegmentsCheckStatus;
    return (r.rowsUploaded ?? 0) > 0
        && rejectedCount(r) === 0
        && validation !== ValidationStatus.FAILED
        && validation !== ValidationStatus.WARNING
        && unknownCheck !== ValidationStatus.FAILED
        && unknownCheck !== ValidationStatus.WARNING;
}

/**
 * Upload an .xlsx of manual values for a segment table. Puts the file in S3 then commits it via
 * UploadSegmentDataTable; a refused upload still returns counters + a report, so we show the summary
 * rather than only an error. On a successful commit it dispatches its own post-upload refresh (and a
 * period switch when the write lands in a different period) from context — no caller callback.
 */
export default function SegmentUploadModal(props: {
    visible: boolean;
    onDismiss: () => void;
}) {
    const { visible, onDismiss } = props;
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    // tableId is the decoded route param published to the detail slice — read from context (reliable +
    // immediate), unlike `definition?.tableId`, which is null until the definition GET resolves and would
    // otherwise let an upload commit against an empty id. The upload op takes no period — the backend
    // resolves it and echoes it back (used for the post-upload view switch, dispatched from here).
    const tableId = state.detail.tableId ?? '';
    const selectedPeriod = state.detail.selectedPeriod;
    const fileInput = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<UploadSegmentDataTableResponse | null>(null);
    // Template download (step 1) is independent of the upload (step 2); track its own loading/error.
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [templateError, setTemplateError] = useState('');
    // Bumped on open, dismiss, and unmount so a still-in-flight upload from a previous open abandons its
    // result instead of banner-ing / refreshing / setting state on a closed or unmounted modal.
    const uploadSeq = useRef(0);
    // The template download (step 1) has its OWN sequence: it must abandon on close/unmount like the
    // upload, but the two flows are independent, so starting an upload must not strand the template
    // download's loading state (and vice versa).
    const templateSeq = useRef(0);

    useEffect(() => {
        if (visible) {
            uploadSeq.current++; // abandon any prior in-flight upload
            templateSeq.current++; // and any prior in-flight template download
            setFile(null);
            setError('');
            setResult(null);
            setUploading(false); // never reopen stuck in a loading state
            setTemplateError('');
            setDownloadingTemplate(false);
        }
    }, [visible]);
    useEffect(() => () => { uploadSeq.current++; templateSeq.current++; }, []); // abandon in-flight work on unmount

    const isXlsx = !!file && file.name.toLowerCase().endsWith('.xlsx');

    // Step 1: download the prefilled template for the selected period. The template op is period-scoped
    // (unlike the upload commit, which resolves the period server-side), so it needs a chosen period.
    // Open the presigned URL via an anchor rather than window.open: the URL resolves after an async
    // fetch, outside the click's user-activation window, where window.open would be popup-blocked.
    const onDownloadTemplate = async () => {
        if (!tableId || !selectedPeriod || downloadingTemplate) {
            return;
        }
        // Abandon-on-close/unmount guard, on the template's OWN sequence: capture it now and only touch
        // state afterwards if it is unchanged, so a dismiss/unmount (or a concurrent upload) mid-fetch
        // can't setState on a dead component or strand the button's loading state.
        const seq = templateSeq.current;
        setTemplateError('');
        setDownloadingTemplate(true);
        try {
            const url = await services.segmentDataTablesService.downloadSegmentUploadTemplate(tableId, selectedPeriod);
            if (url) {
                const a = document.createElement('a');
                a.href = url;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        } catch (ex) {
            if (seq === templateSeq.current) setTemplateError(ErrorUtils.getMessage(ex) || 'Could not download the template.');
        } finally {
            if (seq === templateSeq.current) setDownloadingTemplate(false);
        }
    };

    const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
        setResult(null);
        setError('');
        setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
        e.target.value = ''; // allow re-selecting the same file
    };

    const onUpload = async () => {
        if (!file) {
            return;
        }
        const seq = ++uploadSeq.current;
        setUploading(true);
        setError('');
        try {
            const uploadResult = await services.segmentDataTablesService.uploadSegmentDataTable(tableId, file);
            if (seq !== uploadSeq.current) return; // modal closed/reopened/unmounted mid-upload
            setResult(uploadResult);
            // Clear the chosen file so the Upload button disables — otherwise the same file stays selected
            // and could be committed a second time (a duplicate write). Re-uploading requires re-choosing.
            setFile(null);
            // A refused upload also resolves 200 with counters — only announce success when it was clean,
            // and only refresh the grid when something was actually written.
            if (isCleanUpload(uploadResult)) {
                services.messageService.showSuccessAutoDismissBanner(`${file.name}: ${uploadResult.rowsUploaded ?? 0} row(s) uploaded.`);
            }
            if ((uploadResult.rowsUploaded ?? 0) > 0) {
                // The backend resolves the write to a period it echoes back. Refresh so the period list
                // re-fetches (a newly-materialized period appears in the selector); and if the write landed
                // in a different period, switch the view to it so the new rows are shown. Switch first: the
                // refresh then lands on that period's shared rows, not on a run copy it would default to.
                const writtenPeriod = uploadResult.period;
                if (writtenPeriod && writtenPeriod !== selectedPeriod) {
                    dispatch(ACTIONS.SELECT_DETAIL_PERIOD.withPayload(writtenPeriod));
                }
                dispatch(ACTIONS.REFRESH_DETAIL_ROWS);
            }
        } catch (ex) {
            if (seq !== uploadSeq.current) return;
            setError(ErrorUtils.getMessage(ex) || 'Upload failed.');
        } finally {
            if (seq === uploadSeq.current) setUploading(false);
        }
    };

    // Dismissing must also abandon any in-flight upload (bump the seq) so it can't banner / refresh a
    // modal the user has already closed, and clear the prior result/file so a reopen never flashes it.
    const handleDismiss = () => {
        uploadSeq.current++;
        templateSeq.current++; // abandon an in-flight template download too
        setFile(null);
        setError('');
        setResult(null);
        onDismiss();
    };

    return (
        <Modal
            visible={visible}
            onDismiss={handleDismiss}
            closeAriaLabel="Close upload dialog"
            header="Upload manual values"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleDismiss}>Close</Button>
                        <Button variant="primary" disabled={!isXlsx || uploading} loading={uploading} onClick={onUpload}>
                            Upload
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="l">
                <FormField
                    label="1. Download the template"
                    description="Template includes the segment and its description (locked) and only the manual columns, prefilled with current values. Pull and derived columns are excluded."
                >
                    <SpaceBetween direction="vertical" size="xs">
                        <Button
                            iconName="download"
                            loading={downloadingTemplate}
                            disabled={!selectedPeriod || downloadingTemplate}
                            onClick={onDownloadTemplate}
                        >
                            Download template
                        </Button>
                        {!selectedPeriod && (
                            <StatusIndicator type="info">Select a period first to download its template.</StatusIndicator>
                        )}
                        {templateError && <Alert type="error">{templateError}</Alert>}
                    </SpaceBetween>
                </FormField>
                <FormField label="2. Upload the filled file" description="Only manual columns can be uploaded. Segments are fixed; you can change values but cannot add or remove rows.">
                    <SpaceBetween direction="vertical" size="xs">
                        <Button iconName="upload" onClick={() => fileInput.current?.click()}>Choose file</Button>
                        <input ref={fileInput} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={onFileChosen} />
                        {file && (
                            <StatusIndicator type={isXlsx ? 'success' : 'error'}>
                                {isXlsx ? file.name : `${file.name}: only .xlsx is supported`}
                            </StatusIndicator>
                        )}
                    </SpaceBetween>
                </FormField>
                {error && <Alert type="error">{error}</Alert>}
                {result && (() => {
                    const uploaded = result.rowsUploaded ?? 0;
                    const clean = isCleanUpload(result);
                    // clean → success; nothing written → error; something written but rejections/failed → warning.
                    const type = clean ? 'success' : uploaded > 0 ? 'warning' : 'error';
                    const header = clean ? 'Upload complete' : uploaded > 0 ? 'Upload partially applied' : 'Upload rejected';
                    // A null count means the segment-master check could not run — show that, not a reassuring "0".
                    const unknownSegments = result.unknownSegmentsRejected == null
                        ? 'not checked'
                        : `${result.unknownSegmentsRejected}`;
                    // Only annotate the count with the check status when it flags a problem (WARNING/FAILED).
                    // PASS needs no note, and NOT_APPLICABLE is clean (isCleanUpload treats it so) — showing
                    // "(check NOT_APPLICABLE)" on an otherwise-green success reads as a problem that isn't one.
                    const unknownStatus = result.unknownSegmentsCheckStatus;
                    const unknownSuffix = unknownStatus === ValidationStatus.WARNING || unknownStatus === ValidationStatus.FAILED
                        ? ` (check ${unknownStatus})`
                        : '';
                    // Only show the rejection breakdown when something was actually rejected or the
                    // segment-master check flagged a problem. On a clean success a line starting with
                    // "Rejected." full of zeros reads as a contradiction under the green banner.
                    const showRejected = rejectedCount(result) > 0
                        || unknownStatus === ValidationStatus.WARNING
                        || unknownStatus === ValidationStatus.FAILED;
                    return (
                        <Alert type={type} header={header}>
                            <SpaceBetween size="xxs">
                                <div>Rows uploaded: {uploaded}, matched: {result.rowsMatched ?? 0}, values changed: {result.valuesChanged ?? 0}</div>
                                {showRejected && (
                                    <div>Rejected. Non-editable columns: {result.nonEditableColumnsRejected ?? 0}, unknown segments: {unknownSegments}{unknownSuffix}, out of bounds: {result.valuesOutOfBounds ?? 0}</div>
                                )}
                                {result.validationReport?.overallStatus && (
                                    <div>Validation: {result.validationReport.overallStatus}</div>
                                )}
                            </SpaceBetween>
                        </Alert>
                    );
                })()}
            </SpaceBetween>
        </Modal>
    );
}
